import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, X, Send, Check } from 'lucide-react';
import { Input } from './ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const DELAY_MS = 20000;
const STORAGE_KEY = 'qj_waitlist_popup_v1';

// Suppress on these routes - either the user is already in a signup flow,
// on an admin page, or on their own profile.
const SUPPRESSED_PATHS = [/^\/admin/, /^\/fan\//, /^\/login/, /^\/signup/, /^\/checkout/];

const isSuppressed = (pathname) => SUPPRESSED_PATHS.some((r) => r.test(pathname));

const getState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch { return null; }
};

const setState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* noop */ }
};

export const WaitlistPopup = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState('timer'); // 'timer' | 'exit'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    // Skip immediately if already dismissed / joined / on a suppressed page
    if (isSuppressed(location.pathname)) return;
    const s = getState();
    if (s && (s.joined || s.dismissed)) return;

    let firedTimer = null;
    let armed = true;

    // ---- Trigger 1: 20-second timer ----
    firedTimer = setTimeout(() => {
      const s2 = getState();
      if (!armed || (s2 && (s2.joined || s2.dismissed))) return;
      if (isSuppressed(window.location.pathname)) return;
      armed = false;
      setTrigger('timer');
      setOpen(true);
    }, DELAY_MS);

    // ---- Trigger 2: Exit-intent (desktop only) ----
    // Fires when the mouse leaves the viewport at the top (about to reach the tab bar)
    const isDesktop =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 1024px)').matches &&
      !('ontouchstart' in window);

    const handleMouseLeave = (e) => {
      // Only fire if the cursor exits through the top of the window
      if (e.clientY > 0) return;
      // relatedTarget null = truly left the document (not moved onto another element)
      if (e.relatedTarget) return;
      const s2 = getState();
      if (!armed || (s2 && (s2.joined || s2.dismissed))) return;
      if (isSuppressed(window.location.pathname)) return;
      armed = false;
      setTrigger('exit');
      setOpen(true);
    };

    if (isDesktop) {
      document.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      armed = false;
      if (firedTimer) clearTimeout(firedTimer);
      if (isDesktop) document.removeEventListener('mouseleave', handleMouseLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setState({ dismissed: true, dismissed_at: new Date().toISOString() });
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error('Enter your email');
    setLoading(true);
    try {
      // Carry existing ?ref= if present so popup signups also credit referrers
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      const { data } = await axios.post(`${API_URL}/waitlist`, {
        email: email.trim(),
        name: name.trim(),
        source: trigger === 'exit' ? 'popup_exit' : 'popup',
        ref: ref || undefined,
      });
      setJoined(true);
      setState({ joined: true, joined_at: new Date().toISOString() });
      toast.success(data.message || "You're in!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? 'Please enter a valid email' : (detail || 'Something went wrong');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-popup-title"
      data-testid="waitlist-popup"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-auto"
        onClick={dismiss}
      />

      {/* Panel */}
      <div
        className="relative pointer-events-auto w-full sm:max-w-md glass-card border border-purple-500/30 p-6 sm:p-7 shadow-2xl shadow-purple-900/40 animate-in slide-in-from-bottom-6 sm:zoom-in-95 duration-300"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-gray-500 hover:text-white transition p-1"
          aria-label="Close"
          data-testid="waitlist-popup-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/40">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
            First-Listen Access
          </span>
        </div>

        <h3
          id="waitlist-popup-title"
          className="text-xl sm:text-2xl font-bold mb-2 leading-tight"
          data-testid={`waitlist-popup-title-${trigger}`}
        >
          {trigger === 'exit' ? "Wait — don't leave empty-handed" : 'Enjoying the music?'}
        </h3>
        <p className="text-sm text-gray-400 mb-5">
          {trigger === 'exit'
            ? "Drop your email and be the first to hear the next drop — no need to check back, I'll come to you."
            : "Drop your email and I'll ping you the moment a new track lands — before it hits Spotify or Apple Music."}
        </p>

        {joined ? (
          <div className="flex items-center gap-3 rounded-xl bg-green-500/10 border border-green-400/30 px-4 py-3">
            <div className="w-9 h-9 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <p className="font-semibold text-white text-sm">You're on the list.</p>
              <p className="text-xs text-gray-400">Check your inbox for a welcome note.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2" data-testid="waitlist-popup-form">
            <Input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-dark"
              data-testid="waitlist-popup-name-input"
              disabled={loading}
            />
            <Input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-dark"
              required
              autoFocus
              data-testid="waitlist-popup-email-input"
              disabled={loading}
            />
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={dismiss}
                className="btn-ghost text-sm px-4"
                data-testid="waitlist-popup-dismiss"
              >
                Not now
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="waitlist-popup-submit"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Adding you...' : 'Get First Listen'}
              </button>
            </div>
          </form>
        )}

        <p className="text-[10px] text-gray-500 mt-3 text-center">
          No spam, unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};
